import { supabase } from "@/lib/supabase/client";

export type ObligationType = "reporting" | "notice" | "payment" | "representation" | "other";
export type ObligationFrequency = "one_time" | "quarterly" | "semi_annual" | "annual" | "on_event";
export type ObligationStatus = "active" | "paused" | "completed_one_time" | "expired";

export interface ObligationRow {
  id: string;
  tenant_id: string;
  provision_id: string;
  fund_id: string;
  lp_id: string;
  title: string;
  description: string | null;
  obligation_type: ObligationType;
  frequency: ObligationFrequency | null;
  next_due_date: string | null;
  last_completed_date: string | null;
  assigned_to: string | null;
  status: ObligationStatus;
  created_at: string;
}

const COLS =
  "id, tenant_id, provision_id, fund_id, lp_id, title, description, obligation_type, frequency, next_due_date, last_completed_date, assigned_to, status, created_at";

export async function listObligations(
  tenantId: string,
  opts: { activeOnly?: boolean } = {},
): Promise<ObligationRow[]> {
  // HITL gate (build doc §1.4 / migration 0010): an obligation is DERIVED
  // from its source provision's extracted text — it must never surface (the
  // dashboard widget, the Obligations page, or the audit-pack export) until a
  // human has confirmed or edited that provision. Inner-join on the FK
  // obligations.provision_id -> provisions.id and filter on the joined row,
  // mirroring listProvisionsByFund's activeOnly gate (provisions.ts) and
  // listMfnClausesByFund's provision join (mfn.ts). Unlike activeOnly below
  // (a business status — active/paused/completed/expired), this review-status
  // gate is NOT optional: there is no legitimate reader of obligations that
  // should ever see one derived from a still-pending provision.
  let q = supabase
    .from("obligations")
    .select(`${COLS}, provision:provisions!inner(review_status)`)
    .eq("tenant_id", tenantId)
    .in("provision.review_status", ["confirmed", "edited"])
    .order("next_due_date", { ascending: true, nullsFirst: false });
  if (opts.activeOnly) q = q.eq("status", "active");
  const { data, error } = await q;
  if (error) throw new Error(`listObligations: ${error.message}`);
  return (data ?? []).map((row) => {
    const { provision: _provision, ...obligation } = row as unknown as ObligationRow & {
      provision: { review_status: string };
    };
    return obligation;
  });
}

export interface InsertObligationInput {
  tenant_id: string;
  provision_id: string;
  fund_id: string;
  lp_id: string;
  title: string;
  description: string | null;
  obligation_type: ObligationType;
  frequency: ObligationFrequency | null;
  next_due_date: string | null;
}

export async function insertObligations(rows: InsertObligationInput[]): Promise<number> {
  if (rows.length === 0) return 0;
  const { data, error } = await supabase.from("obligations").insert(rows).select("id");
  if (error) throw new Error(`insertObligations: ${error.message}`);
  return data?.length ?? 0;
}

/** Advance next_due_date by one period after completion. */
function advance(dateIso: string, frequency: ObligationFrequency | null): string | null {
  if (!frequency || frequency === "one_time" || frequency === "on_event") return null;
  const d = new Date(`${dateIso}T00:00:00Z`);
  const months = frequency === "quarterly" ? 3 : frequency === "semi_annual" ? 6 : 12;
  const next = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months + 1, 0));
  return next.toISOString().slice(0, 10);
}

export async function completeObligation(
  obligation: ObligationRow,
  userId: string,
  notes: string,
): Promise<void> {
  const { error: insertError } = await supabase.from("obligation_completions").insert({
    tenant_id: obligation.tenant_id,
    obligation_id: obligation.id,
    completed_by: userId,
    notes: notes || null,
  });
  if (insertError) throw new Error(`completeObligation: ${insertError.message}`);

  const today = new Date().toISOString().slice(0, 10);
  const nextDue = obligation.next_due_date
    ? advance(obligation.next_due_date, obligation.frequency)
    : null;
  const oneTime = obligation.frequency === "one_time" || obligation.frequency === null;
  const { error: updateError } = await supabase
    .from("obligations")
    .update({
      last_completed_date: today,
      next_due_date: nextDue,
      status: oneTime ? "completed_one_time" : "active",
    })
    .eq("id", obligation.id);
  if (updateError) throw new Error(`completeObligation/update: ${updateError.message}`);
}

export interface CompletionRow {
  id: string;
  obligation_id: string;
  completed_at: string;
  completed_by: string | null;
  notes: string | null;
}

export async function listCompletions(obligationId: string): Promise<CompletionRow[]> {
  const { data, error } = await supabase
    .from("obligation_completions")
    .select("id, obligation_id, completed_at, completed_by, notes")
    .eq("obligation_id", obligationId)
    .order("completed_at", { ascending: false });
  if (error) throw new Error(`listCompletions: ${error.message}`);
  return data ?? [];
}
